<template>
  <v-dialog
    v-model="isVisible"
    max-width="800"
    persistent
    scrollable
  >
    <v-card>
      <v-card-title>Notification Editor</v-card-title>
      <v-divider />
      <v-card-text class="pt-6">
        <v-textarea
          ref="notificationTextInput"
          v-model="editedText"
          outlined
        />
        <div class="d-flex align-center text-caption">
          <div class="pr-2">
            Insert text:
          </div>
          <v-divider />
        </div>
        <div class="d-flex flex-wrap pa-1">
          <v-tooltip
            v-for="tag of availableTags"
            :key="tag.tag"
            bottom
          >
            <template #activator="{ on, attr }">
              <v-btn
                class="ma-1"
                depressed
                x-small
                v-bind="attr"
                v-on="on"
                @click="_ => insertTagText(tag)"
              >
                {{ tag.label }}
              </v-btn>
            </template>
            <span>{{ tag.description }}</span>
          </v-tooltip>
        </div>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-btn
          text
          @click="_ => onClose()"
        >
          Cancel
        </v-btn>
        <v-spacer />
        <v-btn
          color="warning"
          text
          @click="_ => onUseTemplate()"
        >
          Use template
        </v-btn>
        <v-spacer />
        <v-btn
          text
          @click="_ => onReset()"
        >
          <v-icon left>
            mdi-restore
          </v-icon>
          Reset
        </v-btn>
        <v-btn
          color="success"
          @click="_ => onSave()"
        >
          <v-icon left>
            save
          </v-icon>
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { getTemplateEntityPropertyValue, parseTemplate, templateTags } from '@/utilities/docket-entry-type-notifications'

export default {
  name: 'NotificationEditorDialog',

  model: {
    event: 'update',
    prop: 'visible',
  },

  props: {
    entities: {
      default: null,
      type: Object,
    },

    template: {
      required: true,
      type: String,
    },

    text: {
      required: true,
      type: String,
    },

    visible: {
      required: true,
      type: Boolean,
    },
  },

  data () {
    return {
      editedText: '',
    }
  },

  computed: {
    availableTags () {
      return templateTags.reduce((res, entry) => {
        const entity = this.templateEntities[entry.entity]
        const value = entity ? getTemplateEntityPropertyValue(entity, entry.property) : null

        res.push({ ...entry, value: value || `[${entry.tag}]` })
        return res
      }, [])
    },

    hasChanges () {
      return this.editedText !== this.text
    },

    isUsingTemplate () {
      return this.editedText === this.templateText
    },

    isVisible: {
      get () { return this.visible },
      set (value) { this.$emit('update', value) },
    },

    templateEntities () {
      return this.entities || {}
    },

    templateText () {
      return parseTemplate(this.template, this.templateEntities)
    },
  },

  watch: {
    templateText (newVal, oldVal) {
      if (this.editedText === oldVal) this.editedText = newVal
      else this.editedText = this.text
    },
  },

  created () {
    this.onReset()
  },

  methods: {
    insertTagText (tag) {
      const txtComponent = this.$refs.notificationTextInput
      const txtInput = txtComponent.$refs.input

      const cursorPos = txtInput.selectionStart || 0
      const selectionSize = (txtInput.selectionEnd || cursorPos) - cursorPos
      const prependText = this.editedText.slice(0, cursorPos)
      const appendText = this.editedText.slice(cursorPos + selectionSize)

      this.editedText = `${prependText}${tag.value}${appendText}`

      this.$nextTick().then(_ => {
        const newCursorPos = cursorPos + tag.value.length
        txtInput.selectionStart = newCursorPos
        txtInput.selectionEnd = newCursorPos
        txtComponent.focus()
      })
    },

    onClose () {
      this.isVisible = false
    },

    onReset () {
      if (this.text) this.editedText = this.text
      else this.editedText = this.templateText
    },

    onSave () {
      if (this.isUsingTemplate) this.$emit('save', '')
      else this.$emit('save', this.editedText)
    },

    onUseTemplate () {
      this.editedText = this.templateText
    },
  },
}
</script>
